<template>
  <basic-dialog
    v-model="dialog"
    :actionButtons="actionButtons"
    :loading="saving"
    :title="team.id ? $t('teams.edit') : $t('teams.create_team')"
  >
    <v-card-text v-if="!loading" class="scroll-content">
      <div :class="$vuetify.breakpoint.xsOnly ? 'caption' : ''" class="mt-4">
        {{ $t('required_fields_are_marked_with_asterisk') }} (<span class="red--text">*</span>).
      </div>
      <v-form ref="form" aria-autocomplete="off">
        <sub-header>{{ $t('teams.settings') }}</sub-header>
        <v-select
          v-if="!typeDisabled"
          v-model="team.type"
          :dense="$vuetify.breakpoint.xsOnly"
          :disabled="disableTeamTypeChange"
          :items="types"
          :label="$t('teams.type')"
          :rules="[$rules.required]"
          class="required"
          outlined
        ></v-select>
        <v-text-field
          v-model="team.name"
          :dense="$vuetify.breakpoint.xsOnly"
          :label="$t('teams.name')"
          :rules="[$rules.required]"
          class="required"
          outlined
          type="search"
        ></v-text-field>
        <v-textarea
          v-model="team.description"
          :dense="$vuetify.breakpoint.xsOnly"
          :label="$t('teams.description')"
          outlined
          rows="3"
        ></v-textarea>

        <sub-header>{{ $t('teams.default_recipients') }}</sub-header>
        <div
          v-if="!$isPaidTenant || ($isPaidTenant && team.answerers && team.answerers.length >= $planLimit.answerers - 10)"
          class="mt-2 mb-5 d-flex">
          <!-- Will be shown if tenant is not paid or if its paid but is 10 recipients away from limit-->
          <div class="mr-2">
            <plan-chip/>
          </div>
          <div class="flex-grow-1">
            {{ $t('teams.max_recipients_x', [$planLimit.answerers, $nextPlan.name]) }}
          </div>
        </div>
        <v-text-field
          v-model="recipients"
          :dense="$vuetify.breakpoint.xsOnly"
          :hint="$t('teams.add_recipients_hint')"
          :label="$t('teams.add_email_to_recipients')"
          outlined
          persistent-hint
          type="search"
          @keypress.enter="addRecipients"
        >
          <template v-slot:append-outer>
            <v-btn :large="$vuetify.breakpoint.smAndUp" class="ma-0 recipients-button" color="primary" outlined
                   @click="addRecipients">
              {{ $t('add') }}
            </v-btn>
          </template>
        </v-text-field>
        <v-alert v-if="notValidEmails.length > 0" class="mt-2 px-3 caption" color="error" dense text>
          <div class="font-weight-bold mb-1">{{ $t('teams.invalid_email_addresses') }}</div>
        </v-alert>

        <v-alert v-if="team.answerers && team.answerers.length > 0" class="mt-2 px-3" color="grey lighten-4" dense>
          <div></div>
          <div v-for="recipient in team.answerers" :key="recipient.email">
            {{ recipient.email }}
            <v-btn color="error" icon small style="margin-top: -1px" @click="deleteRecipient(recipient)">
              <v-icon size="16">mdi mdi-close</v-icon>
            </v-btn>
          </div>
        </v-alert>
      </v-form>
    </v-card-text>

    <v-card-text v-else class="py-10">
      <hb-loading-indicator align-middle/>
    </v-card-text>
    <template v-if="$isTenantAdmin && !!team.key" v-slot:confirm-buttons>
      <confirm-button
        ref="confirmButton"
        :cancel-text="$t('cancel')"
        :loading="saving"
        :stepColors="['error', 'error']"
        :stepMessages="[`${$t('teams.delete_team')}`, `${$t('delete_confirm')}`]"
        text
        @on-confirm-success="handleDeleteTeam()"></confirm-button>
    </template>
  </basic-dialog>
</template>

<script>
import BasicDialog from '@/components/BasicDialog';
import SubHeader from '@/components/SubHeader';
import teamApi from '@/api/team';
import PlanChip from "@/components/Tenant/Reports/PlanChip";
import ConfirmButton from "@/components/ConfirmButton.vue";

export default {
  name: 'EditTeamDialog',
  components: {ConfirmButton, PlanChip, SubHeader, BasicDialog},
  props: ['surveyType'],
  data() {
    return {
      loading: true,
      saving: false,
      dialog: false,
      disableTeamTypeChange: false,
      typeDisabled: false,
      team: {},
      recipients: '',
      types: [
        {value: 'PERSONNEL', text: this.$t('teams.types.PERSONNEL')},
        {value: 'MANAGEMENT', text: this.$t('teams.types.MANAGEMENT')},
      ],
      actionButtons: {
        cancel: {
          click: this.close,
        },
        save: {
          click: this.save,
        },
      },
      notValidEmails: [],
    };
  },
  methods: {
    deleteRecipient(recipient) {
      const rIndex = this.team.answerers.findIndex(r => r.email === recipient.email);
      if (rIndex > -1) {
        this.team.answerers.splice(rIndex, 1);
      }
    },
    addRecipients() {
      if (this.recipients.length === 0) return;
      this.notValidEmails = [];
      let newRecipients = [];
      newRecipients = this.recipients.match(/(?:[a-z0-9+!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/gi);
      if (!newRecipients) {
        this.notValidEmails.push(this.recipients);
      }

      let overLimitFound = false;
      newRecipients.forEach(newRecipient => {
        if (this.team.answerers.findIndex(a => a.email === newRecipient) === -1) {
          if (this.team.answerers.length >= this.$planLimit.answerers) {
            overLimitFound = true;
          } else {
            this.team.answerers.push({email: newRecipient});
          }
        }
      });
      if (overLimitFound) {
        this.$showPlanNotification(this.$t('teams.max_recipients_reached_warning', [this.$nextPlan.name]));
      }
      this.recipients = '';
    },
    close() {
      this.dialog = false;
    },
    async open(key, type) {
      this.loading = true;
      this.team = {
        type: type ? type : null,
        name: '',
        description: '',
        answerers: [],
      };
      if (type) {
        this.typeDisabled = true;
      }
      if (this.surveyType) {
        this.team.type = this.surveyType;
        if (this.surveyType === 'MANAGEMENT') {
          this.disableTeamTypeChange = true;
        }
      }
      this.recipients = '';
      this.dialog = true;
      if (this.team.key) {
        this.$nextTick(() => {
          this.$refs.confirmButton.resetSteps();
        });
      }

      if (key) {
        await this.reloadTeam(key);
      }
      this.loading = false;
    },
    async reloadTeam(key) {
      try {
        const response = await teamApi.getTeam(key);
        if (response) {
          this.team = response;
        }
      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_loading_team'));
      }
    },
    async save() {
      if (!this.$refs.form) return;
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        if (this.team.key) {
          await teamApi.updateTeam(this.team.key, this.team);
          this.$showSuccessNotification(this.$t('teams.team_updated_succesfully'));
        } else {
          await teamApi.createTeam(this.team);
          this.$showSuccessNotification(this.$t('teams.team_created_succesfully'));
        }
        this.dialog = false;
      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_saving_team'));
        console.error(e);
      }
      this.$emit('refresh');
      this.saving = false;
    },
    async handleDeleteTeam() {
      // const confirm = await this.$confirmDelete(this.$t('tenant.confirm_team_delete'), this.$t('tenant.confirm_team_delete_text'));
      // if (!confirm) return;
      if (!this.$isTenantAdmin) return;

      this.saving = true;
      try {
        await teamApi.deleteTeam(this.team.key);
        this.$showSuccessNotification(this.$t('tenant.team_deleted_succesfully'));
        this.$emit('refresh');
      } catch (error) {
        this.$handleApiError(error, this.$t('errors.error_deleting_team'));
      }
      this.saving = false;
      this.close();
    }
  },
  watch: {
    dialog(val) {
      if (!val && this.$refs.form) {
        this.$refs.form.reset();
      }
      if (val && this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.scroll-content {
  max-height: calc(100vh - 240px);
  overflow: auto;
}

@media screen and (min-width: 600px) {
  .scroll-content {
    max-height: calc(100vh - 300px);
    overflow: auto;
  }
}

.recipients-button {
  margin-top: -18px !important;
}

@media screen and (max-width: 599px) {
  .recipients-button {
    margin-top: -8px !important;
    height: 40px !important;
  }
}
</style>
